/**
 * API client for ICP Tests endpoints
 */

import type {
  ICPTestsResponse,
  ICPTestFilters,
  ICPTestWithRanges,
  ICPTestImport,
  ICPTestUpdate,
  ICPImportResponse,
  ICPImportDefaults,
  ICPFileImportOverrides,
  ICPFileImportMissingFieldsDetail,
  ElementHistoryResponse,
  ElementDosingRatesResponse,
} from '@/types/sensor';

/**
 * Thrown by `importICPTestFile` when the backend returns a 422 with a
 * structured missing-fields `detail`. Carries the parsed metadata so the
 * import form can render the confirmation step (generic `handleApiError`
 * only surfaces a message string).
 */
export class ICPFileImportMissingFieldsError extends Error {
  readonly detail: ICPFileImportMissingFieldsDetail;

  constructor(detail: ICPFileImportMissingFieldsDetail) {
    super(detail.message);
    this.name = 'ICPFileImportMissingFieldsError';
    this.detail = detail;
  }
}

/** Response shape for nearest manual test data */
export interface NearestManualTestData {
  [paramKey: string]: {
    value: number;
    timestamp: string;
    delta_seconds: number;
  };
}
import { handleApiError } from '@/utils/apiError';
import { buildApiUrl } from './config';
import { apiClient } from './client';
export const api = {
  /**
   * Fetch ICP tests with optional filtering and pagination
   */
  async getICPTests(tankId: string, filters?: ICPTestFilters): Promise<ICPTestsResponse> {
    const url = new URL(buildApiUrl(`/api/tanks/${tankId}/tests`), window.location.origin);

    // Add query parameters
    if (filters?.tank_name) {
      url.searchParams.append('tank_name', filters.tank_name);
    }
    if (filters?.provider) {
      url.searchParams.append('provider', filters.provider);
    }
    if (filters?.start_date) {
      url.searchParams.append('start_date', filters.start_date);
    }
    if (filters?.end_date) {
      url.searchParams.append('end_date', filters.end_date);
    }
    if (filters?.sort_by) {
      url.searchParams.append('sort_by', filters.sort_by);
    }
    if (filters?.page !== undefined) {
      url.searchParams.append('page', String(filters.page));
    }
    if (filters?.limit !== undefined) {
      url.searchParams.append('limit', String(filters.limit));
    }

    const response = await apiClient(url.toString(), {
      method: 'GET',
      headers: {
      },
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return response.json();
  },


  /**
   * Fetch a single ICP test with reference ranges by barcode
   */
  async getICPTest(tankId: string, barcode: string): Promise<ICPTestWithRanges> {
    const response = await apiClient(buildApiUrl(`/api/tanks/${tankId}/tests/${barcode}`), {
      method: 'GET',
      headers: {
      },
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return response.json();
  },

  /**
   * Import an ICP test from a pasted results link or barcode.
   *
   * Accepts `{ source, provider? }` (provider auto-detected server-side) or
   * the legacy `{ barcode, tank_name }` Reef Zelements payload.
   */
  async importICPTest(tankId: string, data: ICPTestImport): Promise<ICPImportResponse> {
    const response = await apiClient(buildApiUrl(`/api/tanks/${tankId}/tests`), {
      method: 'POST',
      headers: {
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return response.json();
  },

  /**
   * Import an ICP test from an uploaded report file (ATI PDF / Triton CSV).
   *
   * Optional overrides (`test_date`, `barcode`, `tank_label`) come from the
   * confirmation step when the file itself carries no metadata.
   * Note: Content-Type is NOT set manually — the browser sets the
   * multipart boundary for FormData.
   */
  async importICPTestFile(
    tankId: string,
    file: File,
    overrides?: ICPFileImportOverrides
  ): Promise<ICPImportResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (overrides?.test_date) {
      formData.append('test_date', overrides.test_date);
    }
    if (overrides?.barcode) {
      formData.append('barcode', overrides.barcode);
    }
    if (overrides?.tank_label) {
      formData.append('tank_label', overrides.tank_label);
    }

    const response = await apiClient(buildApiUrl(`/api/tanks/${tankId}/tests/file`), {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      // A 422 with a structured detail means the report is missing metadata;
      // surface the parsed values so the form can render the confirm step.
      if (response.status === 422) {
        const body = await response.json().catch(() => null);
        const detail = body?.detail;
        if (detail && typeof detail === 'object' && Array.isArray(detail.missing_fields)) {
          throw new ICPFileImportMissingFieldsError(detail as ICPFileImportMissingFieldsDetail);
        }
        throw new Error(
          `HTTP 422: ${typeof detail === 'string' ? detail : response.statusText}`
        );
      }
      await handleApiError(response);
    }

    return response.json();
  },

  /**
   * Fetch remembered provider-side tank labels for the import form
   */
  async getICPImportDefaults(tankId: string): Promise<ICPImportDefaults> {
    const response = await apiClient(buildApiUrl(`/api/tanks/${tankId}/icp-import-defaults`), {
      method: 'GET',
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return response.json();
  },

  /**
   * Delete an ICP test (cascades to reference ranges)
   */
  async deleteICPTest(tankId: string, barcode: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient(buildApiUrl(`/api/tanks/${tankId}/tests/${barcode}`), {
      method: 'DELETE',
      headers: {
      },
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    // 204 No Content has no body - don't try to parse JSON
    if (response.status === 204) {
      return {
        success: true,
        message: 'ICP test deleted successfully'
      };
    }

    // For other success responses that might have a body
    return response.json();
  },

  /**
   * Update ICP test metadata (tank name, test date)
   */
  async updateICPTest(
    tankId: string,
    barcode: string,
    data: ICPTestUpdate
  ): Promise<{ success: boolean; barcode: string; updated_fields: Record<string, string> }> {
    const response = await apiClient(buildApiUrl(`/api/tanks/${tankId}/tests/${barcode}`), {
      method: 'PUT',
      headers: {},
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return response.json();
  },

  /**
   * Fetch nearest manual test values for all parameters relative to an ICP test
   */
  async getNearestManualTests(tankId: string, barcode: string): Promise<NearestManualTestData> {
    const response = await apiClient(
      buildApiUrl(`/api/tanks/${tankId}/tests/${barcode}/nearest-manual`),
      { method: 'GET' }
    );

    if (!response.ok) {
      await handleApiError(response);
    }

    return response.json();
  },

  /**
   * Fetch element history for specified elements across all ICP tests
   */
  async getElementHistory(tankId: string, elements: string[]): Promise<ElementHistoryResponse> {
    const url = new URL(
      buildApiUrl(`/api/tanks/${tankId}/tests/element-history`),
      window.location.origin
    );
    url.searchParams.append('elements', elements.join(','));

    const response = await apiClient(url.toString(), {
      method: 'GET',
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return response.json();
  },

  /**
   * Fetch trace dosing rates for intervals between ICP test dates
   */
  async getElementDosingRates(tankId: string, testDates: string[]): Promise<ElementDosingRatesResponse> {
    const url = new URL(
      buildApiUrl(`/api/tanks/${tankId}/tests/element-dosing-rates`),
      window.location.origin
    );
    url.searchParams.append('test_dates', testDates.join(','));

    const response = await apiClient(url.toString(), {
      method: 'GET',
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return response.json();
  },
};
